// Rule messages are read directly off each field's existing data-error*
// attributes (already present in account.twig) rather than hardcoded here —
// keeps the twig markup as the single source of truth for copy/i18n.

const EMAIL_RE = /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;

// Same strip set validation.js used for data-type="letters" fields.
const LETTERS_STRIP_RE = /([\\!\(\)|"_№`~;%:?,.//@#$^&*+\-{}\]\[])+/g;

// One entry per field id. `required`'s message prefers data-error-required
// and falls back to data-error — account_first_name/account_last_name only
// carry data-error for their required message, while billing_city/
// billing_address_1/billing_address_number carry both (data-error there is a
// separate format message, not meant for the empty case).
const FIELD_RULES = {
    account_first_name: { required: true, letters: true, minLength: 2, maxLength: 50 },
    account_last_name: { required: true, letters: true, minLength: 2, maxLength: 50 },
    billing_company: {},
    account_email: { required: true, email: true },
    billing_phone: { required: true, digitsMin: 8, digitsMax: 15 },
    billing_postcode: { required: true, exactLength: 4 },
    billing_city: { required: true, minLength: 2, maxLength: 50 },
    billing_address_1: { required: true, minLength: 2, maxLength: 50 },
    billing_address_number: { required: true, maxLength: 50 },
    gender: { required: true },
    date_of_birth: { required: true },
    password_current: { required: true },
    // Password fields carry data-error-lenght (the same site-wide typo'd attribute
    // billing_phone uses), not data-error-length-min like the text fields above.
    new_password: { required: true, minLength: 8, minLengthAttr: 'errorLenght' },
    new_password_confirmation: { required: true, minLength: 8, minLengthAttr: 'errorLenght', matches: 'new_password' },
};

// Pure: returns an error message string, '' (message-less error) or null
// (valid) — never touches the DOM, so it's safe to call for every field on
// every keystroke without flashing errors on fields the user hasn't reached.
function getFieldError(input) {
    const rule = FIELD_RULES[input.id];
    if (!rule) {
        return null;
    }

    const value = input.value;

    if (rule.required && value.length === 0) {
        return input.dataset.errorRequired || input.dataset.error || '';
    }
    if (rule.email && !EMAIL_RE.test(value)) {
        return input.dataset.errorValid || '';
    }
    if (rule.digitsMin || rule.digitsMax) {
        const digits = value.replace(/[^0-9]/g, '');
        if (digits.length < rule.digitsMin || digits.length > rule.digitsMax) {
            return input.dataset.errorLenght || '';
        }
    }
    if (rule.exactLength && value.length !== rule.exactLength) {
        return input.dataset.errorLength || '';
    }
    if (rule.minLength && value.length < rule.minLength) {
        return input.dataset[rule.minLengthAttr || 'errorLengthMin'] || '';
    }
    if (rule.maxLength && value.length > rule.maxLength) {
        return input.dataset.errorLengthMax || '';
    }
    if (rule.matches) {
        const other = document.getElementById(rule.matches);
        if (other && value !== other.value) {
            return input.dataset.errorMatch || '';
        }
    }
    return null;
}

// `.form-field__error` isn't always a direct sibling of the control (it sits
// inside `.form-field__control-wrap` for date_of_birth and `.input-group`
// for the password fields), so search the whole `.form-field` subtree rather
// than assume a fixed depth.
function applyFieldError(input, message) {
    const formField = input.closest('.form-field');
    if (!formField) {
        return;
    }
    const errorEl = formField.querySelector('.form-field__error');
    // message is null (valid), or a string that may itself be '' (an error with
    // no configured message) — so the error/valid branch must key off `!== null`,
    // not string truthiness, or a message-less error would be mistaken for valid.
    const hasError = message !== null;
    formField.classList.toggle('form-field--error', hasError);
    if (errorEl) {
        errorEl.textContent = hasError ? message : '';
    }
}

function validateField(input) {
    const message = getFieldError(input);
    applyFieldError(input, message);
    return message === null;
}

function validateForm(form) {
    let valid = true;
    form.querySelectorAll('.form-field__control').forEach((input) => {
        if (!validateField(input)) {
            valid = false;
        }
    });
    return valid;
}

// Unconditionally enables the button on the first edit rather than gating on
// whole-form validity — a pre-existing empty required field the user isn't
// touching (e.g. date_of_birth on accounts that never went through document
// verification) must not permanently block saving unrelated edits. Real
// validation still happens via validateField/validateForm and the submit
// handler's preventDefault below.
function enableSubmitButton(form) {
    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) {
        submitBtn.disabled = false;
    }
}

function initField(input, form) {
    const onFieldChange = () => {
        if (input.dataset.type === 'letters') {
            input.value = input.value.replace(LETTERS_STRIP_RE, '');
        }
        validateField(input);

        // new_password_confirmation's validity depends on new_password's value, so
        // editing new_password while a confirmation is already typed keeps it in
        // sync instead of leaving a stale match/mismatch state until it's touched.
        if (input.id === 'new_password') {
            const confirmInput = form.querySelector('#new_password_confirmation');
            if (confirmInput && confirmInput.value) {
                validateField(confirmInput);
            }
        }

        enableSubmitButton(form);
    };

    input.addEventListener('input', onFieldChange);
    input.addEventListener('change', onFieldChange);
}

function initForm(form) {
    form.querySelectorAll('.form-field__control').forEach((input) => {
        initField(input, form);
    });

    form.addEventListener('submit', (event) => {
        const valid = validateForm(form);
        if (!valid) {
            event.preventDefault();
        }
    });
}

document.addEventListener('DOMContentLoaded', () => {
    const infoForm = document.querySelector('#account-edit-form');
    const passwordForm = document.querySelector('#account-edit-password');

    if (infoForm) {
        initForm(infoForm);
    }
    if (passwordForm) {
        initForm(passwordForm);
    }
});
